import { ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import { Loader2 } from "lucide-react";
import {
  ClipboardList, Target, BarChart3, Search, Lightbulb,
  AlertTriangle, Tag, Info, FileText, BookOpen, Brain
} from "lucide-react";

const sectionConfig: Record<string, { icon: ReactNode; gradient: string; border: string; bg: string; accent: string }> = {
  "executive overview": {
    icon: <ClipboardList className="h-4 w-4" />,
    gradient: "from-primary to-accent",
    border: "border-primary/20",
    bg: "bg-primary/[0.03]",
    accent: "text-primary",
  },
  "overview": {
    icon: <ClipboardList className="h-4 w-4" />,
    gradient: "from-primary to-accent",
    border: "border-primary/20",
    bg: "bg-primary/[0.03]",
    accent: "text-primary",
  },
  "core arguments": {
    icon: <Target className="h-4 w-4" />,
    gradient: "from-emerald-600 to-teal-500",
    border: "border-emerald-500/20",
    bg: "bg-emerald-500/[0.03]",
    accent: "text-emerald-600 dark:text-emerald-400",
  },
  "core topics": {
    icon: <Target className="h-4 w-4" />,
    gradient: "from-emerald-600 to-teal-500",
    border: "border-emerald-500/20",
    bg: "bg-emerald-500/[0.03]",
    accent: "text-emerald-600 dark:text-emerald-400",
  },
  "key points": {
    icon: <Target className="h-4 w-4" />,
    gradient: "from-emerald-600 to-teal-500",
    border: "border-emerald-500/20",
    bg: "bg-emerald-500/[0.03]",
    accent: "text-emerald-600 dark:text-emerald-400",
  },
  "key data": {
    icon: <BarChart3 className="h-4 w-4" />,
    gradient: "from-cyan-600 to-blue-500",
    border: "border-cyan-500/20",
    bg: "bg-cyan-500/[0.03]",
    accent: "text-cyan-600 dark:text-cyan-400",
  },
  "deep analysis": {
    icon: <Search className="h-4 w-4" />,
    gradient: "from-blue-600 to-indigo-500",
    border: "border-blue-500/20",
    bg: "bg-blue-500/[0.03]",
    accent: "text-blue-600 dark:text-blue-400",
  },
  "content analysis": {
    icon: <Search className="h-4 w-4" />,
    gradient: "from-blue-600 to-indigo-500",
    border: "border-blue-500/20",
    bg: "bg-blue-500/[0.03]",
    accent: "text-blue-600 dark:text-blue-400",
  },
  "detailed summary": {
    icon: <FileText className="h-4 w-4" />,
    gradient: "from-blue-600 to-indigo-500",
    border: "border-blue-500/20",
    bg: "bg-blue-500/[0.03]",
    accent: "text-blue-600 dark:text-blue-400",
  },
  "analysis": {
    icon: <Brain className="h-4 w-4" />,
    gradient: "from-blue-600 to-indigo-500",
    border: "border-blue-500/20",
    bg: "bg-blue-500/[0.03]",
    accent: "text-blue-600 dark:text-blue-400",
  },
  "strategic takeaways": {
    icon: <Lightbulb className="h-4 w-4" />,
    gradient: "from-amber-500 to-orange-500",
    border: "border-amber-500/20",
    bg: "bg-amber-500/[0.03]",
    accent: "text-amber-600 dark:text-amber-400",
  },
  "key takeaways": {
    icon: <Lightbulb className="h-4 w-4" />,
    gradient: "from-amber-500 to-orange-500",
    border: "border-amber-500/20",
    bg: "bg-amber-500/[0.03]",
    accent: "text-amber-600 dark:text-amber-400",
  },
  "expected insights": {
    icon: <Lightbulb className="h-4 w-4" />,
    gradient: "from-amber-500 to-orange-500",
    border: "border-amber-500/20",
    bg: "bg-amber-500/[0.03]",
    accent: "text-amber-600 dark:text-amber-400",
  },
  "critical assessment": {
    icon: <AlertTriangle className="h-4 w-4" />,
    gradient: "from-rose-500 to-red-500",
    border: "border-rose-500/20",
    bg: "bg-rose-500/[0.03]",
    accent: "text-rose-600 dark:text-rose-400",
  },
  "classification": {
    icon: <Tag className="h-4 w-4" />,
    gradient: "from-violet-600 to-purple-500",
    border: "border-violet-500/20",
    bg: "bg-violet-500/[0.03]",
    accent: "text-violet-600 dark:text-violet-400",
  },
  "topics & tags": {
    icon: <Tag className="h-4 w-4" />,
    gradient: "from-violet-600 to-purple-500",
    border: "border-violet-500/20",
    bg: "bg-violet-500/[0.03]",
    accent: "text-violet-600 dark:text-violet-400",
  },
};

function getConfig(title: string) {
  const lower = title.toLowerCase().replace(/[^\w\s&]/g, "").trim();
  for (const key of Object.keys(sectionConfig)) {
    if (lower.includes(key)) return sectionConfig[key];
  }
  return {
    icon: <BookOpen className="h-4 w-4" />,
    gradient: "from-slate-500 to-slate-600",
    border: "border-border",
    bg: "bg-muted/20",
    accent: "text-muted-foreground",
  };
}

interface Section {
  title: string;
  emoji: string;
  content: string;
}

function parseSections(markdown: string): Section[] {
  const lines = markdown.split("\n");
  const sections: Section[] = [];
  let current: Section | null = null;
  const contentLines: string[] = [];

  const flushCurrent = () => {
    if (current) {
      current.content = contentLines.join("\n").trim();
      if (current.content) sections.push(current);
      contentLines.length = 0;
    }
  };

  for (const line of lines) {
    const h2Match = line.match(/^##\s+(.*)/);
    if (h2Match) {
      flushCurrent();
      const raw = h2Match[1].trim();
      const emojiMatch = raw.match(/^(\p{Emoji_Presentation}|\p{Emoji}\uFE0F?)\s*(.*)/u);
      current = {
        emoji: emojiMatch ? emojiMatch[1] : "",
        title: emojiMatch ? emojiMatch[2] : raw,
        content: "",
      };
    } else {
      contentLines.push(line);
    }
  }
  flushCurrent();

  return sections;
}

interface Props {
  summary: string;
  isStreaming?: boolean;
}

export function SummaryRenderer({ summary, isStreaming = false }: Props) {
  const sections = parseSections(summary);

  if (sections.length === 0) {
    return (
      <div className="prose prose-sm dark:prose-invert max-w-none">
        <ReactMarkdown>{summary}</ReactMarkdown>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Table of Contents */}
      {sections.length > 1 && (
        <div className="sticky top-0 z-10 rounded-xl border border-border bg-background/95 backdrop-blur-sm px-4 py-3 shadow-sm">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Contents</h4>
          <nav className="flex flex-wrap gap-x-1 gap-y-1">
            {sections.map((section, i) => {
              const config = getConfig(section.title);
              return (
                <button
                  key={i}
                  onClick={() => document.getElementById(`summary-section-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors hover:opacity-80 ${config.border} ${config.bg} ${config.accent}`}
                >
                  {section.emoji && <span>{section.emoji}</span>}
                  {section.title}
                </button>
              );
            })}
          </nav>
        </div>
      )}

      {sections.map((section, i) => {
        const config = getConfig(section.title);
        const isActive = isStreaming && i === sections.length - 1;
        return (
          <div
            key={i}
            id={`summary-section-${i}`}
            className={`rounded-xl border ${config.border} ${config.bg} overflow-hidden shadow-sm scroll-mt-4 ${isActive ? "ring-1 ring-primary/30" : ""}`}
          >
            <div className={`flex items-center gap-2.5 px-4 py-2.5 bg-gradient-to-r ${config.gradient}`}>
              <div className="p-1 bg-white/20 rounded-md">
                {isActive ? <Loader2 className="h-4 w-4 animate-spin" /> : config.icon}
              </div>
              <h3 className="text-sm font-bold tracking-wide text-white flex-1">
                {section.emoji && <span className="mr-1.5">{section.emoji}</span>}
                {section.title}
              </h3>
              {isActive && (
                <span className="text-[10px] font-medium text-white/70 uppercase tracking-wider">Generating…</span>
              )}
            </div>
            <div className="px-5 py-4">
              <div className="prose prose-sm dark:prose-invert max-w-none
                prose-p:text-foreground/85 prose-p:leading-relaxed prose-p:mb-3
                prose-li:text-foreground/80 prose-li:leading-relaxed
                prose-strong:text-foreground prose-strong:font-semibold
                prose-em:text-muted-foreground
                prose-ul:space-y-2 prose-ol:space-y-2
                prose-li:marker:text-muted-foreground/60
                prose-h3:text-sm prose-h3:font-semibold prose-h3:mt-4 prose-h3:mb-2
                prose-hr:border-border/50 prose-hr:my-4
              ">
                <ReactMarkdown>{section.content}</ReactMarkdown>
              </div>
              {isActive && (
                <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-border/30">
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse [animation-delay:150ms]" />
                  <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse [animation-delay:300ms]" />
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
